import { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  BackLink,
  Badge,
  Button,
  EmptyState,
  Notice,
  PageHead,
  Panel,
  SearchBox,
  Spinner,
  StatBox,
  StatRow,
  Table,
  TableWrap,
  useToast,
} from 'iboon-ui';

import { api, ApiError } from '../api';
import { useIBoonAIContext } from '../iboonAi';
import { openRemoteSupport } from '../remoteSupport';

/**
 * One machine: what it is, who holds it, and -- new here -- what IT can ask
 * it right now.
 *
 * "Run diagnostics" talks to this one device over the pulse socket
 * (hub.send in meshcentral's backend), not to the employee at the keyboard:
 * nothing shows on their screen and nothing asks them first. That is the
 * agreed behaviour for a company machine, and it is also exactly why every
 * pull is on this page and in the audit log -- silent is not the same as
 * unaccountable. See the diagnostics_reports migration and iboon-agent's
 * diagnostics.rs for what is collected and why that line is drawn where it is.
 */

type Device = {
  id: string;
  hostname: string;
  platform: string;
  osVersion: string;
  agentVersion: string;
  online: boolean;
  deregisteredAt: string | null;
  personName: string;
  personEmail: string;
};

type Report = {
  id: string;
  deviceId: string;
  requestedBy: string;
  requestedAt: string;
  receivedAt: string | null;
  status: 'requested' | 'received' | 'failed';
  report: DiagnosticsReport | null;
  error: string | null;
};

type DiagnosticsReport = {
  collectedAt: string;
  agentVersion: string;
  os: {
    version: string;
    uptimeSecs: number;
    pendingReboot: boolean;
    recentHotfixes: Array<{ id: string; installedOn: string }>;
  };
  hardware: {
    cpu: string;
    cores: number;
    memoryTotalBytes: number;
    memoryUsedBytes: number;
    disks: Array<{ name: string; totalBytes: number; freeBytes: number; healthStatus: string | null }>;
    battery: { chargePercent: number | null; healthPercent: number | null; status: string } | null;
  };
  remoteSupport: { installed: boolean; branded: boolean; blockedByWindows: boolean };
  installedApps: Array<{ name: string; version: string; publisher: string }>;
  recentErrors: Array<{ log: string; source: string; time: string; message: string }>;
  warnings: string[];
};

const gb = (bytes: number): string => `${(bytes / 1024 ** 3).toFixed(1)} GB`;

const when = (iso: string | null): string => (iso ? new Date(iso).toLocaleString() : '—');

const hours = (secs: number): string => {
  const h = Math.floor(secs / 3600);
  const d = Math.floor(h / 24);
  return d > 0 ? `${d}d ${h % 24}h` : `${h}h`;
};

/** A request still in flight, worth polling for. */
const isPending = (reports: Report[]): boolean => reports[0]?.status === 'requested';

export function DeviceDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const toast = useToast();

  const [device, setDevice] = useState<Device | null>(null);
  const [reports, setReports] = useState<Report[] | null>(null);
  const [selected, setSelected] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [requesting, setRequesting] = useState(false);
  const pollTimer = useRef<ReturnType<typeof setInterval> | null>(null);
  useIBoonAIContext(
    'device',
    device && {
      id: device.id,
      hostname: device.hostname,
      platform: device.platform,
      osVersion: device.osVersion,
      agentVersion: device.agentVersion,
      online: device.online,
      released: Boolean(device.deregisteredAt),
      person: { name: device.personName, email: device.personEmail },
      latestDiagnostics: reports?.[0]
        ? { id: reports[0].id, status: reports[0].status, requestedAt: reports[0].requestedAt, error: reports[0].error }
        : null,
    },
  );

  const loadReports = useCallback(async () => {
    const result = await api.get<{ reports: Report[] }>(`/api/devices/${id}/diagnostics`);
    setReports(result.reports);
    // Nothing picked yet, or the thing that was picked no longer heads the
    // list (a newer request landed): follow the newest one.
    setSelected((was) => (was && result.reports.some((r) => r.id === was) ? was : (result.reports[0]?.id ?? null)));
    return result.reports;
  }, [id]);

  useEffect(() => {
    // The admin device list already has everything this page's header
    // needs; asking for one device by id would be a second endpoint for one
    // field. Filtering the list is the one extra round trip this avoids.
    api
      .get<{ devices: Device[] }>('/api/devices?includeReleased=true')
      .then((result) => {
        const found = result.devices.find((d) => d.id === id);
        if (!found) {
          setError('No registered device with that id.');
          return;
        }
        setDevice(found);
      })
      .catch((caught: unknown) => setError(caught instanceof Error ? caught.message : 'Could not load it.'));
    loadReports().catch((caught: unknown) => setError(caught instanceof Error ? caught.message : 'Could not load it.'));
  }, [id, loadReports]);

  // Polls while the newest request is still "requested", and stops the
  // moment it is not -- never a timer left running on a page nobody is
  // watching for it.
  useEffect(() => {
    if (!reports || !isPending(reports)) {
      if (pollTimer.current) {
        clearInterval(pollTimer.current);
        pollTimer.current = null;
      }
      return;
    }
    if (pollTimer.current) return;
    pollTimer.current = setInterval(() => {
      loadReports().catch(() => {
        // A poll tick failing is not worth a toast; the next tick tries again.
      });
    }, 2_500);
    return () => {
      if (pollTimer.current) {
        clearInterval(pollTimer.current);
        pollTimer.current = null;
      }
    };
  }, [reports, loadReports]);

  const runDiagnostics = useCallback(async () => {
    if (!id) return;
    setRequesting(true);
    try {
      await api.post(`/api/devices/${id}/diagnostics`, {});
      await loadReports();
    } catch (caught) {
      toast.show(
        caught instanceof ApiError ? caught.message : 'Could not ask for diagnostics.',
        { tone: 'danger' },
      );
    } finally {
      setRequesting(false);
    }
  }, [id, loadReports, toast]);

  if (error) {
    return (
      <Notice tone="danger" title="Could not load that machine">
        {error}
      </Notice>
    );
  }
  if (!device || !reports) return <Spinner />;

  const current = reports.find((r) => r.id === selected) ?? null;
  const pending = isPending(reports);

  return (
    <>
      <BackLink onClick={() => navigate('/devices')}>Back to Devices</BackLink>

      <PageHead
        title={device.hostname || 'unnamed machine'}
        subtitle={`${device.personName} · ${[device.platform, device.osVersion].filter(Boolean).join(' · ')}`}
        actions={
          <>
            {device.platform === 'windows' && (
              <Button
                variant="primary"
                disabled={requesting || pending || !device.online || !!device.deregisteredAt}
                onClick={() => void runDiagnostics()}
              >
                {pending ? 'Collecting…' : 'Run diagnostics'}
              </Button>
            )}
            <Button
              variant="ghost"
              disabled={!device.online || !!device.deregisteredAt}
              onClick={() => openRemoteSupport(device.id, device.personEmail)}
            >
              Remote support
            </Button>
          </>
        }
      />

      {device.deregisteredAt && <Notice tone="neutral">This machine has been released and cannot be reached.</Notice>}
      {!device.deregisteredAt && !device.online && (
        <Notice tone="neutral">
          {device.hostname || 'This machine'} is not online right now. Diagnostics needs its Agent connected.
        </Notice>
      )}
      {device.platform !== 'windows' && !device.deregisteredAt && (
        <Notice tone="neutral">Remote diagnostics covers Windows machines for now.</Notice>
      )}

      <Panel title="Diagnostics history">
        {reports.length === 0 ? (
          <EmptyState title="Nothing asked yet" description="Run diagnostics to see what this machine reports." />
        ) : (
          <TableWrap>
            <Table>
              <thead>
                <tr>
                  <th>Asked</th>
                  <th>By</th>
                  <th>Status</th>
                  <th />
                </tr>
              </thead>
              <tbody>
                {reports.map((r) => (
                  <tr key={r.id} className={r.id === selected ? 'row-selected' : undefined}>
                    <td>{when(r.requestedAt)}</td>
                    <td className="sub">{r.requestedBy || '—'}</td>
                    <td>
                      {r.status === 'received' && <Badge tone="brand">received</Badge>}
                      {r.status === 'requested' && <Badge tone="accent">waiting…</Badge>}
                      {r.status === 'failed' && <Badge tone="danger">failed</Badge>}
                    </td>
                    <td>
                      {r.status !== 'requested' && (
                        <Button variant="ghost" onClick={() => setSelected(r.id)}>
                          View
                        </Button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </Table>
          </TableWrap>
        )}
      </Panel>

      {current?.status === 'failed' && (
        <Notice tone="danger" title="This request did not get a report back">
          {current.error || 'Unknown error.'}
        </Notice>
      )}

      {current?.status === 'received' && current.report && <ReportView report={current.report} />}
    </>
  );
}

function ReportView({ report }: { report: DiagnosticsReport }) {
  return (
    <>
      {report.warnings.length > 0 && (
        <Notice tone="accent" title="Collected with gaps">
          {report.warnings.join(' · ')}
        </Notice>
      )}

      <Panel title={`Collected ${when(report.collectedAt)}`}>
        <StatRow>
          <StatBox value={hours(report.os.uptimeSecs)} label="Uptime" />
          <StatBox value={report.hardware.cores} label="CPU cores" />
          <StatBox value={memoryUsed(report.hardware)} label={`of ${gb(report.hardware.memoryTotalBytes)} RAM used`} />
          <StatBox
            value={report.os.pendingReboot ? <Badge tone="accent">yes</Badge> : 'No'}
            label="Reboot pending"
          />
        </StatRow>
        <p className="sub">
          {[report.os.version, report.hardware.cpu, `Agent ${report.agentVersion}`].filter(Boolean).join(' · ')}
        </p>
      </Panel>

      <Panel title="Disks">
        <TableWrap>
          <Table>
            <thead>
              <tr>
                <th>Drive</th>
                <th>Free</th>
                <th>Total</th>
                <th>Health</th>
              </tr>
            </thead>
            <tbody>
              {report.hardware.disks.map((disk) => {
                const freePct = disk.totalBytes > 0 ? (disk.freeBytes / disk.totalBytes) * 100 : 100;
                const low = freePct < 10;
                return (
                  <tr key={disk.name}>
                    <td>{disk.name}</td>
                    <td className={low ? undefined : 'sub'}>
                      {gb(disk.freeBytes)}
                      {low && (
                        <>
                          {' '}
                          <Badge tone="danger">{Math.round(freePct)}% free</Badge>
                        </>
                      )}
                    </td>
                    <td className="sub">{gb(disk.totalBytes)}</td>
                    <td>
                      {disk.healthStatus === 'OK' && <Badge tone="brand">OK</Badge>}
                      {disk.healthStatus === 'Pred Fail' && <Badge tone="danger">predicting failure</Badge>}
                      {!disk.healthStatus && <span className="sub">—</span>}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </Table>
        </TableWrap>
      </Panel>

      {report.hardware.battery && (
        <Panel title="Battery">
          <StatRow>
            <StatBox
              value={report.hardware.battery.chargePercent != null ? `${Math.round(report.hardware.battery.chargePercent)}%` : '—'}
              label="Charge"
            />
            <StatBox
              value={report.hardware.battery.healthPercent != null ? `${Math.round(report.hardware.battery.healthPercent)}%` : '—'}
              label="Health vs. new"
            />
            <StatBox value={report.hardware.battery.status} label="State" />
          </StatRow>
        </Panel>
      )}

      <Panel title="Remote Support">
        <StatRow>
          <StatBox
            value={
              report.remoteSupport.blockedByWindows ? (
                <Badge tone="danger">blocked by Windows</Badge>
              ) : report.remoteSupport.installed ? (
                <Badge tone="brand">{report.remoteSupport.branded ? 'Remote by iBoon' : 'stock RustDesk'}</Badge>
              ) : (
                <Badge tone="neutral">not installed</Badge>
              )
            }
            label="Status"
          />
        </StatRow>
      </Panel>

      <Panel title={`Recent errors (last 24h, ${report.recentErrors.length})`}>
        {report.recentErrors.length === 0 ? (
          <EmptyState title="Nothing logged" description="No System or Application errors in the last day." />
        ) : (
          <TableWrap>
            <Table>
              <thead>
                <tr>
                  <th>When</th>
                  <th>Log</th>
                  <th>Source</th>
                  <th>Message</th>
                </tr>
              </thead>
              <tbody>
                {report.recentErrors.map((e, i) => (
                  // eslint-disable-next-line react/no-array-index-key -- the
                  // Agent sends no stable id for a log line, and the list is
                  // replaced whole on every report.
                  <tr key={i}>
                    <td className="sub">{when(e.time)}</td>
                    <td>{e.log}</td>
                    <td className="sub">{e.source}</td>
                    <td>{e.message}</td>
                  </tr>
                ))}
              </tbody>
            </Table>
          </TableWrap>
        )}
      </Panel>

      <InstalledApps apps={report.installedApps} />

      {report.os.recentHotfixes.length > 0 && (
        <Panel title="Recent Windows updates">
          <TableWrap>
            <Table>
              <thead>
                <tr>
                  <th>Update</th>
                  <th>Installed</th>
                </tr>
              </thead>
              <tbody>
                {report.os.recentHotfixes.map((h) => (
                  <tr key={h.id}>
                    <td>{h.id}</td>
                    <td className="sub">{h.installedOn}</td>
                  </tr>
                ))}
              </tbody>
            </Table>
          </TableWrap>
        </Panel>
      )}
    </>
  );
}

/**
 * The installed-app list, filtered as you type. A fleet machine runs to
 * several hundred entries, and "is X installed, and which version" is the
 * question this answers -- unusable as a wall of rows you have to scroll.
 */
function InstalledApps({ apps }: { apps: Array<{ name: string; version: string; publisher: string }> }) {
  const [query, setQuery] = useState('');
  const needle = query.trim().toLowerCase();
  const shown = needle
    ? apps.filter(
        (app) =>
          app.name.toLowerCase().includes(needle) || app.publisher.toLowerCase().includes(needle),
      )
    : apps;

  return (
    <Panel title={`Installed apps (${apps.length})`}>
      <div style={{ marginBottom: 12 }}>
        <SearchBox value={query} onValueChange={setQuery} placeholder="Filter by name or publisher…" />
      </div>
      {shown.length === 0 ? (
        <EmptyState title="No match" description={`Nothing installed matches "${query}".`} />
      ) : (
        <TableWrap>
          <Table>
            <thead>
              <tr>
                <th>Name</th>
                <th>Version</th>
                <th>Publisher</th>
              </tr>
            </thead>
            <tbody>
              {shown.map((app, i) => (
                // eslint-disable-next-line react/no-array-index-key -- a fresh,
                // whole list every report, no stable id from the Agent.
                <tr key={i}>
                  <td>{app.name}</td>
                  <td className="sub">{app.version}</td>
                  <td className="sub">{app.publisher}</td>
                </tr>
              ))}
            </tbody>
          </Table>
        </TableWrap>
      )}
    </Panel>
  );
}

/** "6.2 GB (48%)" — the used figure with the percentage that makes it mean something. */
function memoryUsed(hardware: { memoryUsedBytes: number; memoryTotalBytes: number }): string {
  const used = `${(hardware.memoryUsedBytes / 1024 ** 3).toFixed(1)} GB`;
  if (hardware.memoryTotalBytes <= 0) return used;
  return `${used} (${Math.round((hardware.memoryUsedBytes / hardware.memoryTotalBytes) * 100)}%)`;
}
